import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGetForm } from '../hooks/useGetForm';
import { useWallet } from '../hooks/useWallet';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { ConfirmationForm } from '../components';
import { ErrorPage } from '../components/ErrorPage';
import { renderRichText } from '../utils/rich-text';
import { Loader } from '../components/ui/loader';
import type { Form } from '../interfaces/form.interface';

export const ConfirmationPage: React.FC = () => {
  const { wallet } = useParams<{ wallet: string }>();
  
  // Para la URL wallets/:wallet/confirmation
  // Usar el walletId como formId para obtener la configuración
  const { data: formConfig, isLoading: isLoadingTemplate, error: templateError } = useGetForm(wallet || '');
  
  const getWorkspaceId = (config?: Form): string | undefined => {
    if (!config) return undefined;
    return config.workspace;
  };
  
  const workspaceId = getWorkspaceId(formConfig);
  const { data: walletData, isLoading: isLoadingWallet, error: walletError } = useWallet(wallet || '', wallet || '', workspaceId || '');
  
  usePageMetadata(formConfig);
  const [hideLogo, setHideLogo] = useState(false);

  if (isLoadingTemplate || isLoadingWallet) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader size="lg" id={wallet!} />
      </div>
    );
  }

  if (templateError || walletError || !formConfig) {
    return (
      <ErrorPage 
        error="No se pudo cargar la configuración de confirmación."
        statusCode={404}
      />
    );
  }

  const { metadata } = formConfig;
  const configuration = formConfig;
  const workspace = { metadata: walletData?.wallet.metadata || {} } as Record<string, unknown>;

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{
        backgroundImage: configuration.theme?.visual?.background?.image ? `url(${configuration.theme.visual.background.image})` : undefined,
        backgroundColor: configuration.theme?.visual?.background?.color || 'rgb(236, 169, 43)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
      }}
    >
      {/* Header */}
      <div 
        className="min-h-[70px] w-full"
        style={{
          backgroundColor: configuration.theme?.visual?.header?.background || '#f5f5f5',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
        }}
      >
        <div className="flex justify-center w-full p-5">
          <h1 
            className="text-xl text-center"
            style={{ color: configuration.theme?.visual?.header?.textColor || '#FFF' }}
          >
            {configuration.theme?.visual?.header?.title && renderRichText(configuration.theme.visual.header.title)}
          </h1>
        </div>
      </div>

      {/* Logo Section */}
      {!hideLogo && (
        <div className="flex justify-center relative my-10">
          {configuration.theme?.visual?.background?.blur && configuration.theme?.visual?.logo?.image && (
            <div 
              className="absolute w-full h-full filter blur-6 opacity-40"
              style={{
                background: `url(${configuration.theme.visual.logo.image})`,
                filter: 'blur(1.5rem)',
                opacity: 0.4,
              }}
            />
          )}
          
          {configuration.theme?.visual?.logo?.image && (
            <div className="w-full max-w-[600px] flex justify-center z-20">
              <img 
                src={configuration.theme.visual.logo.image}
                alt="Logo"
                style={{ height: configuration.theme.visual.logo.height || 'auto' }}
                className="max-w-full h-auto"
              />
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="flex justify-center w-full">
        <div className="max-w-[600px] w-full px-4 pb-8">
          <ConfirmationForm
            metadata={metadata}
            configuration={configuration}
            workspace={workspace}
            walletId={wallet || ''}
            onErrorChange={(visible: boolean) => setHideLogo(visible)}
          />
        </div>
      </div>

      {/* Footer */}
      {configuration.theme?.visual?.footer?.image && (
        <div 
          className="flex justify-center py-10"
          style={{ marginBottom: configuration.theme.visual.footer.marginBottom || '170px' }}
        >
          <img 
            src={configuration.theme.visual.footer.image}
            alt="Footer"
            style={{ 
              height: configuration.theme?.visual?.footer?.height || 'auto',
              width: '100%',
              maxWidth: '500px'
            }}
          />
        </div>
      )}
    </div>
  );
};
